import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Tabs,
  Text,
} from "@mantine/core";
import { IconDownload } from "@tabler/icons";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import DataTable from "./dataTable";
import { getLicenceRoleLabel, getSfcRoleLabel } from "../data/licenceRoles";
import { openFileUrl } from "../utils/openReport";

const numYears = 3;

const EarnedNeedCell = ({ earned = 0, need = 0 }) => {
  const needDisplay = Math.max(0, need ?? 0);
  const met = (earned ?? 0) >= needDisplay;
  return (
    <Text size="xs" color={met ? "cyan" : "red"} weight={500}>
      {earned ?? 0} / {needDisplay}
    </Text>
  );
};

const earnedNeedColumn = (headerName, earnedField, needField) => ({
  field: earnedField,
  headerName,
  cellRender: (col, data) => (
    <EarnedNeedCell earned={data[earnedField]} need={data[needField]} />
  ),
});

const getIaStartYear = () => {
  const currentYear = moment().year();
  return moment().isBefore(`${currentYear}-08-01`)
    ? currentYear
    : currentYear + 1;
};

const buildIaYearOptions = (startYear) => {
  const yrs = [];
  for (let i = 0; i < numYears; i++) {
    const yr = startYear - i;
    yrs.push({
      label: `${yr - 1} - ${yr}`,
      value: yr,
    });
  }
  return yrs;
};

const buildCalendarYearOptions = (startYear) => {
  const yrs = [];
  for (let i = 0; i < numYears; i++) {
    const yr = startYear - i;
    yrs.push({
      label: `${yr}`,
      value: yr,
    });
  }
  return yrs;
};

const getPeriodLabel = (rows, startField, endField) => {
  const first = rows?.[0];
  if (!first?.[startField] || !first?.[endField]) return null;
  return `${first[startField]} → ${first[endField]}`;
};

const ConsultantNameCell = ({ data, dimmed = false }) => {
  const displayName = [data.name, data.cname, data.aliasName]
    .filter(Boolean)
    .join(", ");

  return (
    <Text size="xs" color={dimmed ? "dimmed" : undefined} lineClamp={2}>
      {displayName || "—"}
    </Text>
  );
};

const CpdSummaryPanel = ({
  yearOptions,
  defaultYear,
  fetchData,
  downloadReport,
  columns,
  periodFields,
}) => {
  const { t } = useTranslation();
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [rows, setRows] = useState([]);
  const [periodLabel, setPeriodLabel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadData = async (year) => {
    try {
      setLoading(true);
      const result = await fetchData(year, { skipExport: true });
      const data = result?.data?.result ?? [];
      setRows(data);
      if (periodFields) {
        setPeriodLabel(
          getPeriodLabel(data, periodFields.start, periodFields.end)
        );
      }
    } catch (error) {
      console.log(error);
      setRows([]);
      setPeriodLabel(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(defaultYear);
  }, [defaultYear]);

  const handleYearChange = (year) => {
    setSelectedYear(year);
    loadData(year);
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const result = await downloadReport(selectedYear);
      if (result?.data?.url) {
        await openFileUrl(result.data.url);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack spacing="md">
      <Group position="apart">
        <Group>
          <Select
            data={yearOptions}
            value={selectedYear}
            onChange={handleYearChange}
            size="xs"
            w={140}
          />
          {periodLabel && (
            <Text size="xs" color="dimmed">
              {t("Period")}: {periodLabel}
            </Text>
          )}
          {!loading && (
            <Text size="xs" color="dimmed">
              {rows.length} {t("consultants")}
            </Text>
          )}
        </Group>
        <Button
          size="xs"
          leftIcon={<IconDownload size={14} />}
          onClick={handleDownload}
          loading={downloading}
        >
          {t("Download")}
        </Button>
      </Group>

      {loading ? (
        <Group position="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : (
        <DataTable
          hideSearchBar={false}
          data={rows}
          hidePagination={true}
          columns={columns}
        />
      )}
    </Stack>
  );
};

const CpdCreditsSummary = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [activeTab, setActiveTab] = useState("IA");
  const [visitedTabs, setVisitedTabs] = useState(new Set(["IA"]));

  const iaStartYear = useMemo(() => getIaStartYear(), []);
  const calendarStartYear = useMemo(() => moment().year(), []);
  const iaYearOptions = useMemo(
    () => buildIaYearOptions(iaStartYear),
    [iaStartYear]
  );
  const calendarYearOptions = useMemo(
    () => buildCalendarYearOptions(calendarStartYear),
    [calendarStartYear]
  );

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setVisitedTabs((prev) => new Set([...prev, tab]));
  };

  const iaColumns = [
    {
      field: "staffId",
      headerName: t("Staff ID"),
      sortable: true,
    },
    {
      field: "name",
      headerName: t("Name"),
      sortable: true,
      cellRender: (col, data) => <ConsultantNameCell data={data} />,
    },
    {
      field: "iaNumber",
      headerName: t("IA Number"),
      cellRender: (col, data) => data.iaNumber || "—",
    },
    {
      field: "iaRole",
      headerName: t("IA Role"),
      cellRender: (col, data) => getLicenceRoleLabel(data.iaRole, t) || "—",
    },
    earnedNeedColumn(t("Total"), "totalCredit", "totalNeed"),
    earnedNeedColumn(t("IA Credit"), "IACredit", "ia_need"),
    {
      field: "IAERCreditEffective",
      headerName: t("IAER Credit"),
      cellRender: (col, data) => (
        <EarnedNeedCell
          earned={
            data.IAERCreditEffective ??
            (data.IAERCredit ?? 0) + (data.IAROCredit ?? 0)
          }
          need={data.iaer_need}
        />
      ),
    },
    earnedNeedColumn(t("IA RO Credit"), "IAROCredit", "ia_ro_need"),
    earnedNeedColumn(t("ILAS Credit"), "IAILASCredit", "ia_ilas_need"),
  ];

  const mpfColumns = [
    {
      field: "staffId",
      headerName: t("Staff ID"),
      sortable: true,
    },
    {
      field: "name",
      headerName: t("Name"),
      sortable: true,
      cellRender: (col, data) => <ConsultantNameCell data={data} />,
    },
    {
      field: "mpfaNumber",
      headerName: t("MPFA Number"),
      cellRender: (col, data) => data.mpfaNumber || "—",
    },
    earnedNeedColumn(t("Core"), "MPFCoreCredit", "mpf_core_need"),
    earnedNeedColumn(t("Non-Core"), "MPFNonCoreCredit", "mpf_noncore_need"),
    {
      field: "total",
      headerName: t("Total"),
      cellRender: (col, data) => (
        <EarnedNeedCell
          earned={(data.MPFCoreCredit ?? 0) + (data.MPFNonCoreCredit ?? 0)}
          need={(data.mpf_core_need ?? 0) + (data.mpf_noncore_need ?? 0)}
        />
      ),
    },
  ];

  const sfcColumns = [
    {
      field: "staffId",
      headerName: t("Staff ID"),
      sortable: true,
    },
    {
      field: "name",
      headerName: t("Name"),
      sortable: true,
      cellRender: (col, data) => (
        <ConsultantNameCell
          data={data}
          dimmed={data.sfcEligible === false}
        />
      ),
    },
    {
      field: "sfcNumber",
      headerName: t("SFC Number"),
      cellRender: (col, data) => data.sfcNumber || "—",
    },
    {
      field: "sfcRole",
      headerName: t("SFC Role"),
      cellRender: (col, data) => getSfcRoleLabel(data.sfcRole, t) || "—",
    },
    {
      field: "sfcEligible",
      headerName: t("Eligible"),
      cellRender: (col, data) =>
        data.sfcEligible ? (
          <Badge color="green" variant="light" size="xs">
            {t("Yes")}
          </Badge>
        ) : (
          <Badge color="gray" variant="light" size="xs">
            {t("No")}
          </Badge>
        ),
    },
    earnedNeedColumn(t("Total CPT"), "totalCredit", "totalNeed"),
    {
      field: "SFCCredit",
      headerName: t("SFC Credit"),
      cellRender: (col, data) => (
        <Text size="xs">{data.SFCCredit ?? 0}</Text>
      ),
    },
    earnedNeedColumn(t("EC Credit"), "SFCECCredit", "sfc_ec_need"),
  ];

  return (
    <Tabs value={activeTab} onTabChange={handleTabChange} variant="outline">
      <Tabs.List mb="md">
        <Tabs.Tab value="IA">IA</Tabs.Tab>
        <Tabs.Tab value="MPF">MPF</Tabs.Tab>
        <Tabs.Tab value="SFC">SFC</Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="IA">
        {visitedTabs.has("IA") && (
          <CpdSummaryPanel
            yearOptions={iaYearOptions}
            defaultYear={iaStartYear}
            fetchData={(year, opts) => api.Cpd.getIAYearReport(year, opts)}
            downloadReport={(year) => api.Cpd.getIAYearReport(year)}
            columns={iaColumns}
            periodFields={{ start: "IAStartDate", end: "IADueDate" }}
          />
        )}
      </Tabs.Panel>

      <Tabs.Panel value="MPF">
        {visitedTabs.has("MPF") && (
          <CpdSummaryPanel
            yearOptions={calendarYearOptions}
            defaultYear={calendarStartYear}
            fetchData={(year, opts) => api.Cpd.getMPFYearReport(year, opts)}
            downloadReport={(year) => api.Cpd.getMPFYearReport(year)}
            columns={mpfColumns}
            periodFields={{ start: "MPFStartDate", end: "MPFDueDate" }}
          />
        )}
      </Tabs.Panel>

      <Tabs.Panel value="SFC">
        {visitedTabs.has("SFC") && (
          <CpdSummaryPanel
            yearOptions={calendarYearOptions}
            defaultYear={calendarStartYear}
            fetchData={(year, opts) => api.Cpd.getSFCYearReport(year, opts)}
            downloadReport={(year) => api.Cpd.getSFCYearReport(year)}
            columns={sfcColumns}
            periodFields={{ start: "SFCStartDate", end: "SFCDueDate" }}
          />
        )}
      </Tabs.Panel>
    </Tabs>
  );
};

export default CpdCreditsSummary;
